import { useRef, useCallback } from "react";

/**
 * Handles batched value toggling for drag-select scenarios.
 *
 * useDragSelect fires parentOnChange N times synchronously on mouseup.
 * Without batching, each call reads the same stale value array and only
 * the last toggle survives. This hook chains each call off the previous
 * result via a ref, then clears after the microtask.
 *
 * @returns {function} toggle({ key, fallbackValues, option, buildEntry }) => newValues
 *   - key: grouping key (use a constant for single-value, filterId for multi-value)
 *   - fallbackValues: current values array when no in-flight state exists
 *   - option: the option being toggled (must have _id or value)
 *   - buildEntry: (option, optionId) => entry object to add when selecting
 */
export const useInFlightToggle = () => {
  const ref = useRef({});

  const toggle = useCallback(({ key, fallbackValues, option, buildEntry }) => {
    const currentValues = key in ref.current
      ? ref.current[key]
      : (fallbackValues || []);

    const optionId = option._id ?? option.value;
    const isSelected = currentValues.some((v) => v.value === optionId);
    const newValues = isSelected
      ? currentValues.filter((v) => v.value !== optionId)
      : [...currentValues, buildEntry(option, optionId)];

    ref.current[key] = newValues;
    queueMicrotask(() => { delete ref.current[key]; });

    return newValues;
  }, []);

  return toggle;
};
